import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import {
  ArrowRight,
  Check,
  Printer,
  RotateCcw,
  Trophy,
  Users,
  X,
} from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { usePageView } from "@/hooks/use-page-view";
import { DECK, LEVELS, deckFor, shuffled, type DeckCard, type DeckLevel } from "@/data/deck";

const TITLE = "The Smartasaurus Card Deck - free trivia game";
const DESC =
  "60 cards, three difficulty levels, built-in scoreboard. Play solo or pass the phone around. Free, no email, and printable as a real card deck.";

export const Route = createFileRoute("/deck")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://smartasaurus.com/deck" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/deck" }],
  }),
  component: DeckPage,
});

const BEST_KEY = "smartasaurus.deck.best";
const ROUND = 10;

type Player = { name: string; score: number; answered: number };

/** "You" reads as "Your turn"; names ending in s take a bare apostrophe. */
function turnLabel(name?: string): string {
  const n = (name ?? "").trim();
  if (!n) return "Your turn";
  if (n.toLowerCase() === "you") return "Your turn";
  return `${n}${n.toLowerCase().endsWith("s") ? "'" : "'s"} turn`;
}

function loadBest(): Record<string, number> {
  if (typeof window === "undefined") return {};
  try {
    return JSON.parse(window.localStorage.getItem(BEST_KEY) ?? "{}");
  } catch {
    return {};
  }
}

function DeckPage() {
  usePageView("page");

  const [level, setLevel] = useState<DeckLevel | null>(null);
  const [names, setNames] = useState<string[]>(["You"]);
  const [started, setStarted] = useState(false);
  const [cards, setCards] = useState<DeckCard[]>([]);
  const [idx, setIdx] = useState(0);
  const [turn, setTurn] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [best, setBest] = useState<Record<string, number>>({});

  useEffect(() => setBest(loadBest()), []);

  const card = cards[idx];
  const order = useMemo(
    () => (card ? shuffled(card.options.map((_, i) => i)) : []),
    [card],
  );

  function start(lv: DeckLevel) {
    const pool = shuffled(deckFor(lv)).slice(0, ROUND);
    setCards(pool);
    setLevel(lv);
    setIdx(0);
    setTurn(0);
    setPicked(null);
    setPlayers(names.map((n) => ({ name: n.trim() || "Player", score: 0, answered: 0 })));
    setStarted(true);
  }

  function answer(optIdx: number) {
    if (picked !== null || !card) return;
    setPicked(optIdx);
    const correct = optIdx === card.a;
    setPlayers((p) =>
      p.map((pl, i) =>
        i === turn
          ? { ...pl, score: pl.score + (correct ? 10 : 0), answered: pl.answered + 1 }
          : pl,
      ),
    );
  }

  function next() {
    setPicked(null);
    setTurn((t) => (t + 1) % players.length);
    setIdx((i) => i + 1);
  }

  const finished = started && idx >= cards.length;

  useEffect(() => {
    if (!finished || !level || players.length !== 1) return;
    const s = players[0].score;
    setBest((b) => {
      if ((b[level] ?? 0) >= s) return b;
      const nb = { ...b, [level]: s };
      try {
        window.localStorage.setItem(BEST_KEY, JSON.stringify(nb));
      } catch {
        /* ignore */
      }
      return nb;
    });
  }, [finished, level, players]);

  const ranked = [...players].sort((a, b) => b.score - a.score);

  function shareResult() {
    if (!level) return;
    const lv = LEVELS.find((l) => l.key === level)!.name;
    const text =
      players.length === 1
        ? `I scored ${players[0].score}/${cards.length * 10} on the ${lv} deck at Smartasaurus. Beat it: https://smartasaurus.com/deck`
        : `${ranked[0].name} won our Smartasaurus card deck night (${ranked[0].score} pts). Your turn: https://smartasaurus.com/deck`;
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({ text }).catch(() => {});
    } else if (typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(text);
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <main id="main-content" className="print:hidden">
        {/* ------------------------------------------------------- HEADER */}
        <section className="border-b border-border/60 px-4 pb-10 pt-12 md:px-8 md:pt-16">
          <div className="mx-auto max-w-4xl">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
              · Free game · no email
            </div>
            <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] md:text-6xl">
              The Smartasaurus{" "}
              <span className="text-gradient-curiosity">card deck</span>
            </h1>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
              60 cards. Three levels - starting at "you already know this".
              Scores are counted for you. Play alone or pass the phone around the
              table.
            </p>
          </div>
        </section>

        {/* -------------------------------------------------------- SETUP */}
        {!started && (
          <section className="px-4 py-10 md:px-8 md:py-14">
            <div className="mx-auto max-w-4xl">
              <div className="rounded-2xl border border-border/60 bg-card p-5 md:p-7">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  <Users className="h-4 w-4 text-primary" />
                  Who's playing?
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {names.map((n, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        value={n}
                        onChange={(e) =>
                          setNames((v) => v.map((x, j) => (j === i ? e.target.value : x)))
                        }
                        placeholder={`Player ${i + 1}`}
                        className="w-full rounded-xl border border-border/60 bg-background px-4 py-3 text-base outline-none focus:border-primary"
                      />
                      {names.length > 1 && (
                        <button
                          type="button"
                          aria-label={`Remove player ${i + 1}`}
                          onClick={() => setNames((v) => v.filter((_, j) => j !== i))}
                          className="rounded-full border border-border p-2 text-muted-foreground hover:text-foreground"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {names.length < 6 && (
                  <button
                    type="button"
                    onClick={() => setNames((v) => [...v, `Player ${v.length + 1}`])}
                    className="mt-4 rounded-full border border-border px-5 py-2 text-sm uppercase tracking-[0.15em] text-muted-foreground transition hover:text-foreground"
                  >
                    + Add player
                  </button>
                )}
              </div>

              <div className="mt-5 grid gap-4 md:grid-cols-3">
                {LEVELS.map((l) => (
                  <button
                    key={l.key}
                    type="button"
                    onClick={() => start(l.key)}
                    className="group rounded-2xl border border-border/60 bg-card p-6 text-left transition hover:border-primary"
                  >
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
                      {l.tag}
                    </div>
                    <div className="mt-2 font-display text-3xl uppercase">{l.name}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{l.blurb}</p>
                    <div className="mt-4 flex items-center justify-between text-xs uppercase tracking-[0.2em] text-muted-foreground">
                      <span>Best: {best[l.key] ?? 0} pts</span>
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1 group-hover:text-primary" />
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-border/60 bg-card p-6 md:flex md:items-center md:justify-between">
                <div>
                  <div className="font-display text-2xl uppercase">
                    Want it on the table?
                  </div>
                  <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                    Print all 60 cards plus a scorecard, cut them out, and play with
                    no phone at all. Same game, paper edition.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => typeof window !== "undefined" && window.print()}
                  className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-3 font-display text-base uppercase tracking-wide text-primary-foreground transition hover:opacity-90 md:mt-0"
                >
                  <Printer className="h-4 w-4" />
                  Print the deck
                </button>
              </div>
            </div>
          </section>
        )}

        {/* --------------------------------------------------------- PLAY */}
        {started && !finished && card && (
          <section className="px-4 py-8 md:px-8 md:py-12">
            <div className="mx-auto max-w-3xl">
              <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <span>
                  Card {idx + 1} / {cards.length} · {card.cat}
                </span>
                <span className="text-primary">{turnLabel(players[turn]?.name)}</span>
              </div>

              <div className="mt-4 rounded-3xl border border-border/60 bg-card p-6 shadow-xl md:p-9">
                <p className="font-display text-2xl uppercase leading-tight md:text-4xl">
                  {card.q}
                </p>

                <div className="mt-6 grid gap-3">
                  {order.map((oi) => {
                    const isCorrect = oi === card.a;
                    const isPicked = picked === oi;
                    const revealed = picked !== null;
                    return (
                      <button
                        key={oi}
                        type="button"
                        disabled={revealed}
                        onClick={() => answer(oi)}
                        className={[
                          "flex items-center justify-between gap-3 rounded-xl border px-5 py-4 text-left text-base transition",
                          revealed && isCorrect
                            ? "border-primary bg-primary/10"
                            : revealed && isPicked
                              ? "border-destructive/60 bg-destructive/10"
                              : "border-border/60 bg-background hover:border-primary hover:bg-primary/5",
                        ].join(" ")}
                      >
                        <span>{card.options[oi]}</span>
                        {revealed && isCorrect && <Check className="h-5 w-5 text-primary" />}
                        {revealed && isPicked && !isCorrect && (
                          <X className="h-5 w-5 text-destructive" />
                        )}
                      </button>
                    );
                  })}
                </div>

                {picked !== null && (
                  <div className="mt-6 rounded-2xl border border-primary/40 bg-primary/5 p-5">
                    <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary">
                      {picked === card.a ? "+10 points" : "Why you were wrong"}
                    </div>
                    <p className="mt-2 text-base text-muted-foreground">{card.why}</p>
                    <button
                      type="button"
                      onClick={next}
                      className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-display text-base uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
                    >
                      Next card
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* live scoreboard */}
              <div className="mt-5 rounded-2xl border border-border/60 bg-card p-5">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
                  Scoreboard
                </div>
                <div className="mt-3 grid gap-2">
                  {players.map((p, i) => (
                    <div
                      key={i}
                      className={[
                        "flex items-center justify-between rounded-lg px-3 py-2 text-sm",
                        i === turn ? "bg-primary/10 text-foreground" : "text-muted-foreground",
                      ].join(" ")}
                    >
                      <span>{p.name}</span>
                      <span className="font-display text-lg">{p.score}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ------------------------------------------------------ RESULTS */}
        {finished && (
          <section className="px-4 py-10 md:px-8 md:py-16">
            <div className="mx-auto max-w-3xl rounded-3xl border border-primary/40 bg-card p-6 md:p-9">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary">
                <Trophy className="h-4 w-4" />
                Final scoreboard
              </div>
              <div className="mt-5 grid gap-2">
                {ranked.map((p, i) => (
                  <div
                    key={p.name + i}
                    className="flex items-center justify-between rounded-xl border border-border/60 px-4 py-3"
                  >
                    <span className="font-display text-xl uppercase">
                      {i + 1}. {p.name}
                    </span>
                    <span className="font-display text-2xl text-primary">{p.score}</span>
                  </div>
                ))}
              </div>
              {level && players.length === 1 && (
                <p className="mt-4 text-sm text-muted-foreground">
                  Personal best on {LEVELS.find((l) => l.key === level)!.name}:{" "}
                  {Math.max(best[level] ?? 0, players[0].score)} pts.
                </p>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => level && start(level)}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 font-display text-base uppercase tracking-wide text-primary-foreground transition hover:opacity-90"
                >
                  <RotateCcw className="h-4 w-4" />
                  Play again
                </button>
                <button
                  type="button"
                  onClick={() => setStarted(false)}
                  className="rounded-full border border-border px-6 py-3 font-display text-base uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                >
                  Change level
                </button>
                <button
                  type="button"
                  onClick={shareResult}
                  className="rounded-full border border-border px-6 py-3 font-display text-base uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                >
                  Share score
                </button>
                <Link
                  to="/discoveries"
                  className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 font-display text-base uppercase tracking-wide text-muted-foreground transition hover:text-foreground"
                >
                  Keep exploring
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* -------------------------------------------------- PRINT EDITION */}
      <PrintDeck />

      <div className="print:hidden">
        <SiteFooter />
      </div>
    </div>
  );
}

function PrintDeck() {
  return (
    <div className="hidden print:block">
      <style>{`
        @media print {
          @page { margin: 10mm; }
          body { background: #fff !important; }
          .print-card { break-inside: avoid; }
        }
      `}</style>
      <div style={{ color: "#000", fontFamily: "sans-serif" }}>
        {/* Print-only masthead - a second <h1> would compete with the page's real one. */}
        <p style={{ fontSize: 22, fontWeight: 800, margin: "0 0 4px" }}>
          SMARTASAURUS CARD DECK
        </p>
        <p style={{ fontSize: 11, margin: "0 0 12px" }}>
          Cut along the boxes. Read the question out loud, hide the answer with a
          thumb. 10 points for a correct answer. smartasaurus.com/deck
        </p>

        {/* scorecard */}
        <table
          style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, marginBottom: 16 }}
        >
          <thead>
            <tr>
              <th style={cell}>Player</th>
              {Array.from({ length: 10 }).map((_, i) => (
                <th key={i} style={cell}>
                  {i + 1}
                </th>
              ))}
              <th style={cell}>Total</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }).map((_, r) => (
              <tr key={r}>
                <td style={{ ...cell, height: 22 }} />
                {Array.from({ length: 10 }).map((_, i) => (
                  <td key={i} style={cell} />
                ))}
                <td style={cell} />
              </tr>
            ))}
          </tbody>
        </table>

        {LEVELS.map((l) => (
          <div key={l.key}>
            <h2 style={{ fontSize: 15, margin: "14px 0 6px" }}>
              {l.name.toUpperCase()} - {l.tag}
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {DECK.filter((c) => c.level === l.key).map((c, i) => (
                <div
                  key={c.id}
                  className="print-card"
                  style={{
                    border: "1px dashed #444",
                    borderRadius: 6,
                    padding: 8,
                    fontSize: 10,
                    minHeight: 110,
                  }}
                >
                  <div style={{ fontSize: 8, letterSpacing: 1, marginBottom: 3 }}>
                    {l.name.toUpperCase()} · {c.cat.toUpperCase()} · #{i + 1}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                    {c.q}
                  </div>
                  {c.options.map((o, oi) => (
                    <div key={oi}>
                      {String.fromCharCode(65 + oi)}. {o}
                    </div>
                  ))}
                  <div style={{ marginTop: 4, borderTop: "1px solid #bbb", paddingTop: 3 }}>
                    <strong>Answer: {String.fromCharCode(65 + c.a)}.</strong> {c.why}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const cell: React.CSSProperties = {
  border: "1px solid #999",
  padding: "3px 4px",
  textAlign: "left",
};
